import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  Injectable,
  Injector,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  NgbDateParserFormatter,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  SmsCreditsUpdatePayload,
  TopUp,
} from './models/SmsSubscriptionCredits';
import { SmsCreditSubscriptionStateService } from './services/sms-credit-subscription-state.service';
import {
  renewalDateForSubscriptionChnageValidator,
  changeSubscriptionEffectiveDateValidator,
  changeSubscriptionCreditsValidator,
  dateNotInThePastValidator,
  orderNumberValidator,
  topUpCreditsValidator,
  topUpEffectiveDateValidator,
  topUpExpirationDateValidator,
} from './validators/validators';

@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly delimeter = '/';
  public parse(value: string): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.delimeter);
      return { day: +date[0], month: +date[1], year: +date[2] };
    }
    return null;
  }

  public format(date: NgbDateStruct | null): string {
    const { day, month, year } = date || {};
    return date
      ? `${year}${this.delimeter}${month}${this.delimeter}${day}`
      : '';
  }
}

@Component({
  selector: 'app-sms-credit-subscription',
  templateUrl: './sms-credit-subscription.component.html',
  styleUrls: ['./sms-credit-subscription.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
})
export class SmsCreditSubscriptionComponent {
  public readonly current$ = this.smsCreditSubscriptionStateService.current$;
  public readonly upcomingSubscription$ = this.smsCreditSubscriptionStateService
    .upcomingSubscription$;
  public readonly subscriptionChange$ = this.smsCreditSubscriptionStateService
    .subscriptionChange$;
  public readonly topUps$ = this.smsCreditSubscriptionStateService.topUps$;

  public readonly renewalDateLoading$ = this.smsCreditSubscriptionStateService
    .renewalDateLoading$;

  public readonly packageLoading$ = this.smsCreditSubscriptionStateService
    .packageLoading$;

  public readonly topUpLoading$ = this.smsCreditSubscriptionStateService
    .topUpLoading$;

  public readonly upcomingSubscriptionEffectiveDate$ = this
    .smsCreditSubscriptionStateService
    .upcomingSubscriptionEffectiveDateSubject$;

  public readonly credits$ = combineLatest([
    this.current$,
    this.upcomingSubscription$,
  ]).pipe(
    map(([current, upcoming]) => ({
      current,
      upcoming,
      remainingCredits:
        (upcoming?.credits ?? 0) -
        (current?.totalSubscriptionCredits ?? 0) +
        (current?.totalCredits ?? 0),
    })),
  );

  public readonly activeTopupCredits$ = combineLatest([
    this.topUps$,
    this.subscriptionChange$,
  ]).pipe(
    filter(
      ([topUps, subscriptionChange]) =>
        !!topUps?.length && !!subscriptionChange,
    ),
    map(([topUps, subscriptionChange]) => {
      const effectiveDate = new Date(
        subscriptionChange?.effectiveDate,
      ).getTime();
      const isActive = (topUp: TopUp) =>
        effectiveDate >= new Date(topUp.effectiveDate).getTime() &&
        effectiveDate <= new Date(topUp.expiryDate).getTime();
      return topUps
        .filter(isActive)
        .reduce((acc, topUp) => acc + topUp.credits, 0);
    }),
  );

  public readonly emptySubscription$ = combineLatest([
    this.current$,
    this.upcomingSubscription$,
    this.topUps$,
  ]).pipe(
    map(
      ([current, upcoming, topUps]) =>
        !current?.totalCredits && !upcoming?.credits && !topUps?.length,
    ),
  );

  public packageFormFields = [];
  public emptyPackageFormFields = [];
  public topUpFormFields = [];
  public renewalFormFields = [];
  public orderingFormsFields = [];
  public orderingEmptyFormFields = [];

  public form: FormGroup;

  private accountId: string;

  public get emptyPackageGroup() {
    return this.form.get('emptyPackage') as FormGroup;
  }

  public get packageGroup() {
    return this.form.get('package') as FormGroup;
  }

  public get topUpGroup() {
    return this.form.get('topUp') as FormGroup;
  }

  public get renewalGroup() {
    return this.form.get('renewal') as FormGroup;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly injector: Injector,
    public readonly smsCreditSubscriptionStateService: SmsCreditSubscriptionStateService,
  ) {
    this.form = this.fb.group({
      renewal: this.fb.group({
        renewalDate: [
          null,
          [
            Validators.required,
            renewalDateForSubscriptionChnageValidator(this.injector),
          ],
        ],
      }),
      package: this.fb.group({
        credits: [
          '',
          [
            Validators.required,
            changeSubscriptionCreditsValidator(this.injector),
          ],
        ],
        effectiveDate: [
          null,
          [
            Validators.required,
            changeSubscriptionEffectiveDateValidator(this.injector),
          ],
        ],
        orderNumber: ['', [Validators.required, orderNumberValidator]],
        validators: [],
      }),
      emptyPackage: this.fb.group({
        credits: [
          '',
          [Validators.required, Validators.pattern(/^[1-9][0-9]*$/)],
        ],
        effectiveDate: [null, [Validators.required, dateNotInThePastValidator]],
        orderNumber: ['', [Validators.required, orderNumberValidator]],
      }),
      topUp: this.fb.group({
        credits: ['', [topUpCreditsValidator, Validators.required]],
        effectiveDate: [
          null,
          [Validators.required, topUpEffectiveDateValidator],
        ],
        expirationDate: [null, [topUpExpirationDateValidator('effectiveDate')]],
        orderNumber: ['', [Validators.required, orderNumberValidator]],
      }),
    });

    this.initForm();

    this.accountId = this.route.snapshot.paramMap.get('id');
    this.smsCreditSubscriptionStateService.getSmsSubscriptionCredits(
      this.accountId,
    );
  }

  public trackById(_: number, { id }) {
    return id;
  }

  public removeUpcomingSubscription(id: number) {
    this.smsCreditSubscriptionStateService.removeUpcomingSubscription(
      this.accountId,
      id,
    );
  }

  public removeUpcomingTopUp(id: number) {
    this.smsCreditSubscriptionStateService.removeUpcomingTopUp(
      this.accountId,
      id,
    );
  }

  public updateRenewalDate() {
    const { renewalDate } = this.renewalGroup.value;
    if (!renewalDate) {
      return;
    }
    const { year, month, day } = renewalDate;
    const payload: SmsCreditsUpdatePayload = {
      subscriptionRenewalDate: new Date(Date.UTC(year, month - 1, day))
        .toISOString()
        .slice(0, 10),
    };
    this.smsCreditSubscriptionStateService.renewalDateUpdate(
      this.accountId,
      payload,
    );
  }

  public resetCredits() {
    this.smsCreditSubscriptionStateService.resetCredits(this.accountId);
  }

  public changePackage(group: FormGroup) {
    const { credits, effectiveDate, orderNumber } = group.value;
    if (!effectiveDate || !credits || !orderNumber) {
      return;
    }
    const { year, month, day } = effectiveDate;
    const payload: SmsCreditsUpdatePayload = {
      subscriptionCredits: Number(credits),
      effectiveDate: new Date(Date.UTC(year, month - 1, day))
        .toISOString()
        .slice(0, 10),
      orderNumber: orderNumber,
    };
    this.smsCreditSubscriptionStateService.packageUpdate(
      this.accountId,
      payload,
    );
    group.reset();
  }

  public changeTopUp() {
    const {
      credits,
      effectiveDate,
      expirationDate,
      orderNumber,
    } = this.topUpGroup.value;
    const { year, month, day } = effectiveDate;
    if (!effectiveDate || !credits || !orderNumber) {
      return;
    }
    let payload: SmsCreditsUpdatePayload = {
      topUpCredits: Number(credits),
      effectiveDate: new Date(Date.UTC(year, month - 1, day))
        .toISOString()
        .slice(0, 10),
      orderNumber: orderNumber,
    };
    if (expirationDate) {
      const { year: expYear, month: expMonth, day: expDay } = expirationDate;
      const expiryDate = new Date(Date.UTC(expYear, expMonth - 1, expDay))
        .toISOString()
        .slice(0, 10);
      payload = { ...payload, expiryDate };
    }
    this.smsCreditSubscriptionStateService.topUpUpdate(this.accountId, payload);
    this.topUpGroup.reset();
  }

  private initForm() {
    this.renewalFormFields = [
      {
        type: 'date',
        description: 'Subscription credits automatic renewal date *',
        formControl: this.renewalGroup.get('renewalDate'),
      },
    ];

    this.packageFormFields = [
      {
        description: 'Change subscription credits *',
        formControl: this.packageGroup.get('credits'),
      },
      {
        type: 'date',
        description: 'Effective date *',
        formControl: this.packageGroup.get('effectiveDate'),
      },
      {
        description: 'Order number *',
        formControl: this.packageGroup.get('orderNumber'),
      },
    ];

    this.emptyPackageFormFields = [
      {
        description: 'Subscription credits *',
        formControl: this.emptyPackageGroup.get('credits'),
      },
      {
        type: 'date',
        description: 'Effective date *',
        formControl: this.emptyPackageGroup.get('effectiveDate'),
      },
      {
        description: 'Order number *',
        formControl: this.emptyPackageGroup.get('orderNumber'),
      },
    ];

    this.topUpFormFields = [
      {
        description: 'Top up with credits *',
        formControl: this.topUpGroup.get('credits'),
      },
      {
        type: 'date',
        description: 'Effective date *',
        formControl: this.topUpGroup.get('effectiveDate'),
      },
      {
        type: 'date',
        description: 'Expiry date (30 days if not provided)',
        formControl: this.topUpGroup.get('expirationDate'),
      },
      {
        description: 'Order number *',
        formControl: this.topUpGroup.get('orderNumber'),
      },
    ];

    this.orderingEmptyFormFields = [
      {
        group: this.emptyPackageGroup,
        fields: this.emptyPackageFormFields,
        messages: {
          wrongDate: 'Must not be in the past',
          pattern: 'Please enter a valid number',
        },
        buttonLabel: 'Change',
        action: () => this.changePackage(this.emptyPackageGroup),
        loading$: this.packageLoading$,
        disabled: of(false),
      },
      {
        group: this.topUpGroup,
        fields: this.topUpFormFields,
        messages: {
          pattern: 'Top-up credits must be greater than 0',
          wrongTopUpCredits: 'Top-up credits must be greater than 0',
          wrongTopUpEffectiveDate: 'Top-up effective date must be after today',
          wrongTopUpExpirationDate:
            'Top-up expiry date must be after today and after top-up effective date',
        },
        buttonLabel: 'Top up',
        action: () => this.changeTopUp(),
        loading$: this.topUpLoading$,
        disabled: of(false),
      },
    ];

    this.orderingFormsFields = [
      {
        group: this.renewalGroup,
        fields: this.renewalFormFields,
        messages: {
          wrongDate:
            'Automatic subscription renewal date must be after today and after the existing subscription credit change',
        },
        buttonLabel: 'Update',
        action: () => this.updateRenewalDate(),
        loading$: this.renewalDateLoading$,
        disabled: of(false),
      },
      {
        group: this.packageGroup,
        fields: this.packageFormFields,
        messages: {
          pattern:
            'Subscription credit change must be higher than the current subscription credits',
          wrongSubscriptionCredits:
            'Subscription credit change credits must be greater than 0 and higher than the current subscription credits',
          wrongDate:
            'Subscription credit change date must be after today and before the automatic subscription renewal date',
        },
        buttonLabel: 'Change',
        action: () => this.changePackage(this.packageGroup),
        loading$: this.packageLoading$,
        disabled$: this.subscriptionChange$,
      },
      {
        group: this.topUpGroup,
        fields: this.topUpFormFields,
        messages: {
          pattern: 'Top-up credits must be greater than 0',
          wrongTopUpCredits: 'Top-up credits must be greater than 0',
          wrongTopUpEffectiveDate: 'Top-up effective date must be after today',
          wrongTopUpExpirationDate:
            'Top-up expiry date must be after today and after top-up effective date',
        },
        buttonLabel: 'Top up',
        action: () => this.changeTopUp(),
        loading$: this.topUpLoading$,
        disabled: of(false),
      },
    ];
  }

  private toEffectiveData(date: string) {
    const year = new Date(date).getFullYear();
    const month = new Date(date).getMonth() + 1;
    const day = new Date(date).getDate();
    return { year, month, day };
  }
}
